/**
 * Role presets for one-click agent creation, plus the deterministic canvas colours
 * that keep the Flow home readable: every preset role owns a colour, and anything
 * else (a custom role, a repository) gets a stable hue derived from its identifier so
 * the same graph always paints the same way. Nothing here touches the network.
 */

export interface AgentRolePreset {
  role: string;
  label: string;
  hint: string;
  color: string;
  icon: string;
}

export const AGENT_ROLE_PRESETS: readonly AgentRolePreset[] = [
  { role: "implementer", label: "Implementer", hint: "builds changes on a branch", color: "#2563eb", icon: "build" },
  { role: "reviewer", label: "Reviewer", hint: "reviews diffs and reports back", color: "#7c3aed", icon: "review" },
  { role: "merge", label: "Merge", hint: "integrates branches", color: "#059669", icon: "merge" },
  { role: "qa", label: "QA", hint: "tests and verifies", color: "#d97706", icon: "test" },
  { role: "planner", label: "Planner", hint: "breaks work down", color: "#0891b2", icon: "plan" },
  { role: "docs", label: "Docs", hint: "writes documentation", color: "#db2777", icon: "docs" },
];

const GENERAL_AGENT_COLOR = "#5f6fff";

/** The colour a role should carry when an agent is created for it; always #rrggbb, as the API requires. */
export function roleColor(role: string | null): string {
  if (role === null) return GENERAL_AGENT_COLOR;
  const preset = AGENT_ROLE_PRESETS.find((candidate) => candidate.role === role);
  return preset ? preset.color : hexAccentFor(role);
}

export const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

/** Same stable hue as accentFor, expressed as a hex colour for inputs and the API. */
export function hexAccentFor(seed: string): string {
  const hue = hueFor(seed);
  const saturation = 0.62;
  const lightness = 0.42;
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const secondary = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
  const match = lightness - chroma / 2;
  const [red, green, blue] = hue < 60 ? [chroma, secondary, 0]
    : hue < 120 ? [secondary, chroma, 0]
      : hue < 180 ? [0, chroma, secondary]
        : hue < 240 ? [0, secondary, chroma]
          : hue < 300 ? [secondary, 0, chroma]
            : [chroma, 0, secondary];
  const channel = (value: number) => Math.round((value + match) * 255).toString(16).padStart(2, "0");
  return `#${channel(red)}${channel(green)}${channel(blue)}`;
}

function hueFor(seed: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619) >>> 0;
  }
  return hash % 360;
}

/** Lowercase ASCII slug for handles and roles, matching the contracts' HandleSchema. */
export function slugify(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48);
}

/** A stable, legible accent for an arbitrary identifier such as a repository ID. */
export function accentFor(seed: string): string {
  return `hsl(${hueFor(seed)} 62% 42%)`;
}
